import type { HawbRef } from "./manifest";
import type { Product } from "./products";
import type { RfidTag } from "./rfidTag";
import type { Site } from "./sites";

export enum ItemType {
  ITEM = "item",
  CAGE = "cage",
  CAGE_ITEM = "cage_item",
}

/**
 * Item form / save payload. `null` means "clear this field" and is sent as
 * such; `undefined` means "untouched" and is dropped from the request. Status
 * is never preselected: it is one of the category's scan steps, chosen by
 * the user, and `null` when the item has no category.
 */
export interface CreateItemPayload {
  rfid_ids?: number[];
  product_id: number | string | null | undefined;
  date_of_circulation: Date | string | null;
  next_maintenance_date: Date | string | null;
  last_maintenance_date: Date | string | null;
  location: string | null;
  notify_email: string | null;
  external_id?: string | null;
  customize_columns: Record<string, string>;
  site_id: number | string | null;
  status: ItemStatus | string | null;
  /** System-tracked; the backend defaults it, the form never preselects it. */
  maintenance_status: string | null;
  /** System-tracked; the backend defaults it, the form never preselects it. */
  retrieval_status: string | null;
  stolen: boolean;
  type?: ItemType;
  cage_id?: number | null;
  cage_item_ids?: number[];
}

export enum ItemStatus {
  IN_STOCK_MAIN = "in_stock_main",
  IN_TRANSIT_TO_SUB = "in_transit_to_sub",
  INBOUND_SUB = "inbound_sub",
  IN_STOCK_SUB = "in_stock_sub",
  ISSUED_TO_FIELD = "issued_to_field",
  ON_SITE = "on_site",
  IN_STOCK = "in_stock",
  IN_DELIVERY = "in_delivery",
  IN_RETRIEVAL = "in_retrieval",
  IN_MAINTENANCE = "in_maintenance",
  MISSING = "missing",
  INCOMPLETE = "incomplete",
  DELIVERED = "delivered",
  STORM_STOCK = "storm_stock",
}

export enum StatusType {
  INTERNAL = "internal",
  EXTERNAL = "external",
  EXITED = "exited",
  MAINTENANCE = "maintenance",
  AT_CUSTOMER = "at_customer",
}

export enum MaintenanceStatus {
  IMPENDING = "impending",
  ON_TIME = "on_time",
  IN_PROGRESS = "in_progress",
  LATE = "late",
}
export enum RetrievalStatus {
  IMPENDING = "impending",
  ON_TIME = "on_time",
  IN_PROGRESS = "in_progress",
  LATE = "late",
}

export interface Item {
  id: number;
  rfid_tags: RfidTag[];
  product_id: number;
  date_of_circulation: string; // ISO string
  next_maintenance_date: string; // ISO string
  last_maintenance_date: string; // ISO string
  location: string;
  notify_email: string;
  external_id?: string;
  /** Null until a status is chosen (the backend never picks one itself). */
  status: ItemStatus | string | null;
  retrieval_status: RetrievalStatus;
  maintenance_status: MaintenanceStatus;
  latitude: number | null;
  longitude: number | null;
  scan_count: number;
  site_id: number | null;
  customize_columns: {
    [key: string]: string;
  };
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  last_moved?: string | null; // ISO string — when the item last changed status
  product: Product;
  site: Site;
  stolen?: boolean;
  type?: ItemType;
  cage?: Item | null;
  cage_items?: Item[];
  /** How many items this one is linked to. Present on list responses. */
  related_count?: number;
  /** How many times the item was scanned. Present on list responses only when
   * the client has the "Times scanned" column enabled. */
  times_scanned?: number;
  /** Consignment this item is a package of; null for ordinary items. */
  hawb?: HawbRef | null;
  tag_id?: string;
  last_scan_data?: {
    time: string;
    type: 'scan' | 'refill'
  }
}

export interface SendMessagePayload {
  subject: string;
  message: string;
}

/**
 * One pin of GET /item/map (dashboard Stock Alerts). Deliberately slimmer than
 * `Item`: only what the pin tooltip shows. Relations are null when unknown.
 */
export interface MapDataItem {
  id: number;
  external_id: string | null;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  status: string;
  maintenance_status: MaintenanceStatus | null;
  rfid_tags: { rfid: string }[];
  product: { id: number; name: string } | null;
  site: { id: number; name: string; latitude: number | null; longitude: number | null } | null;
  /** Sub-client whose geofence contains the item (nearest when several), same rule as the sub-client page. */
  sub_client: { id: number; name: string } | null;
}

export interface MapData {
  items: MapDataItem[];
  counts: Record<string, number>;
}
